import React, { useState, useEffect } from 'react';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Checkbox } from 'components/ui/checkbox';
import { 
  RefreshCw, Plus, Trash2, Pause, Play, Edit, Calendar, Clock, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export default function RecurringTasks({ circleId }) {
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    recurrence_pattern: 'daily',
    recurrence_days: [],
    recurrence_time: '09:00',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  useEffect(() => {
    fetchRecurringTasks();
  }, [circleId]);

  const fetchRecurringTasks = async () => {
    try {
      const url = circleId
        ? `/recurring-tasks?circle_id=${circleId}`
        : '/recurring-tasks';
      const response = await api.get(url);
      setRecurringTasks(response.data.recurring_tasks || []);
    } catch (error) {
      console.error('Failed to fetch recurring tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        circle_id: circleId,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      await api.post('/recurring-tasks', payload);
      toast.success('Recurring task created!');
      setShowCreate(false);
      setFormData({
        title: '',
        description: '',
        recurrence_pattern: 'daily',
        recurrence_days: [],
        recurrence_time: '09:00',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      fetchRecurringTasks();
    } catch (error) {
      toast.error('Failed to create recurring task');
    }
  };

  const togglePause = async (task) => {
    try {
      const endpoint = task.is_active
        ? `/recurring-tasks/${task.recurring_id}/pause`
        : `/recurring-tasks/${task.recurring_id}/resume`;

      await api.post(endpoint, {});
      toast.success(task.is_active ? 'Task paused' : 'Task resumed');
      fetchRecurringTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (recurringId) => {
    if (!confirm('Delete this recurring task?')) return;
    
    try {
      await api.delete(`/recurring-tasks/${recurringId}`, {
        data: { delete_future_tasks: true }
      });
      toast.success('Recurring task deleted');
      fetchRecurringTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleDay = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(dayValue)
        ? prev.recurrence_days.filter(d => d !== dayValue)
        : [...prev.recurrence_days, dayValue]
    }));
  };

  const getPatternLabel = (pattern, days) => {
    if (pattern === 'daily') return 'Daily';
    if (pattern === 'weekly') {
      const dayLabels = days.map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean);
      return dayLabels.length > 0 ? dayLabels.join(', ') : 'Weekly';
    }
    if (pattern === 'monthly') {
      return days.length > 0 ? `Day ${days.join(', ')} monthly` : 'Monthly';
    }
    return pattern;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recurring-tasks">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-teal-500" />
          Recurring Tasks
        </h3>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              New Recurring Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Recurring Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Task Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Morning medication"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add details..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Recurrence Pattern</Label>
                <Select 
                  value={formData.recurrence_pattern}
                  onValueChange={(v) => setFormData({ ...formData, recurrence_pattern: v, recurrence_days: [] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.recurrence_pattern === 'weekly' && (
                <div className="space-y-2">
                  <Label>Days of Week</Label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          formData.recurrence_days.includes(day.value)
                            ? 'bg-teal-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.recurrence_pattern === 'monthly' && (
                <div className="space-y-2">
                  <Label>Day of Month (1-31)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.recurrence_days[0] || ''}
                    onChange={(e) => setFormData({ ...formData, recurrence_days: [parseInt(e.target.value)] })}
                    placeholder="e.g., 1, 15"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={formData.recurrence_time}
                    onChange={(e) => setFormData({ ...formData, recurrence_time: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {recurringTasks.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <RefreshCw className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No recurring tasks</p>
            <p className="text-sm text-slate-400">Create tasks that repeat automatically</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {recurringTasks.map((task) => (
            <Card key={task.recurring_id} className={!task.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900">{task.title}</h4>
                      {!task.is_active && <Badge variant="secondary">Paused</Badge>}
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {getPatternLabel(task.recurrence_pattern, task.recurrence_days)}
                      </span>
                      {task.recurrence_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {task.recurrence_time}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      onClick={() => togglePause(task)}
                      title={task.is_active ? 'Pause' : 'Resume'}
                    >
                      {task.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                      onClick={() => deleteTask(task.recurring_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
